import { Types } from './header';
import { bufferedHandler } from '../../tools/effects';
import { all, select, put, delay } from 'redux-saga/effects';
import { ee } from '../../../services/earth-engine';
import { Actions as Imagery } from '../imagery';
import {
  applyScaleFactors,
  applyUiBand,
  applyNdviBand,
  applyMndwiBand,
  maskCloudLandsat,
  maskCloudSentinel,
  getLandCoverLabelMapBiomas,
} from '../../../algorithms/euscoast';
import { Actions as EUSCoast } from './index';
import { getAcquisitionParameters } from '../../../selectors';
import { aoiGeometry } from '../../../common/aoi';

/**
 * @function handleTransformClassToPolygons
 * @description Vectorizes a classified image into land-cover polygons and dispatches the
 * resulting water and non-water land-cover geometries.
 * 1. Vectorizes the classification within the AOI — using a MapBiomas-specific reducer/label
 *    remapping when `type` is "mapbiomas", or the classifier's own labels otherwise.
 * 2. Extracts the "water" (label 2) polygon separately from the remaining land-cover polygons.
 * 3. Dispatches the water geometry, the per-code land-cover geometries, and the undissolved
 *    vectors (one polygon per painted area) that the result page exports as a shapefile.
 * @param {Object} params.payload - Action payload.
 * @param {ee.Image} params.payload.classification - Classified image to vectorize.
 * @param {ee.Projection} params.payload.imageProjection - Classification image's projection.
 * @param {Number} params.payload.year - Year the classification belongs to.
 * @param {String} params.payload.type - Classification type (e.g. "mapbiomas").
 * @param {Array} params.payload.AOICoords - Area of interest polygon coordinates.
 * @generator
 * @yields {Object} EUSCoast.setWaterLandCover - Dispatches the vectorized water geometry.
 * @yields {Object} EUSCoast.setClassLandCovers - Dispatches the vectorized land-cover geometries.
 * @yields {Object} EUSCoast.setClassLandCoverPolygons - Dispatches the individual class polygons.
 * @see {@link getLandCoverLabelMapBiomas}
 * **Called by:
 * @see {ProcessingPage} - EUSCoast module's processing page (imported as ProcessingEUSCoastPage), via Types.TRANSFORM_CLASS_TO_POLYGONS.
 * @memberof module:Euscoast
 * @returns {void}
 */
const handleTransformClassToPolygons = function* ({
  payload: { classification, imageProjection, year, type, AOICoords },
}) {
  var AOI = aoiGeometry(AOICoords);

  let polyggonized,
    reduceParams = {
      geometry: AOI,
      scale: 30,
      eightConnected: true,
      crs: imageProjection,
    };

  let vectorizedFeatures = [];

  if (type === 'mapbiomas') {
    reduceParams = {
      reducer: ee.Reducer.countEvery(),
      geometry: AOI,
      scale: 30,
      maxPixels: 1e8,
    };

    polyggonized = classification.reduceToVectors(reduceParams);

    var polyggonizedList = { ...polyggonized.getInfo() };

    polyggonizedList.features.forEach((feature, i) => {
      var label = feature.properties.label;

      polyggonizedList.columns.label = 'Integer';
      polyggonizedList.features[i].properties.label = getLandCoverLabelMapBiomas(label);
    });

    polyggonized = ee.FeatureCollection(polyggonizedList);
    vectorizedFeatures = polyggonizedList.features;
  } else {
    polyggonized = classification.reduceToVectors(reduceParams);

    try {
      vectorizedFeatures = polyggonized.getInfo().features;
    } catch (error) {
      console.error('Falha ao obter os poligonos vetorizados para exportacao:', error);
    }
  }

  let justWater = polyggonized.filter(ee.Filter.eq('label', 2));

  let landCoversCode = polyggonized.distinct('label').aggregate_array('label').getInfo();

  let landCoversGeometry = landCoversCode.map((code) => {
    return {
      id: code,
      year,
      geometry: polyggonized.filter(ee.Filter.eq('label', code)).geometry().getInfo(),
    };
  });

  let waterOutPut = {
    year,
    geometry: justWater.geometry().getInfo(),
  };

  yield put(EUSCoast.setWaterLandCover(waterOutPut));
  yield put(EUSCoast.setClassLandCovers(year, landCoversGeometry));
  yield put(EUSCoast.setClassLandCoverPolygons(year, vectorizedFeatures));
};

/**
 * @function handleAcquireImagesMedian
 * @description Builds a median composite image per requested year, generates its true-color and
 * false-color thumbnails, and pushes each as an image layer.
 * 1. For each year, acquires the first image for each requested date, mosaics them, adds the
 *    UI/NDVI/MNDWI bands, applies Landsat scale factors if needed, masks clouds, and clips to
 *    the AOI. EUSCoast applies no elevation mask - urban growth is measured at any elevation.
 * 2. Renders true-color and false-color thumbnail URLs from the composite.
 * 3. Dispatches each year's layer to the imagery state, spaced by 1 second to avoid overloading
 *    Earth Engine.
 * @param {Object} params.payload - Action payload.
 * @param {Array} params.payload.years - Selected years, as [year, imageInfos] entries.
 * @generator
 * @yields {Object} select(getAcquisitionParameters) - Reads the AOI geometry and satellite config.
 * @yields {Object} Imagery.pushImageUrl - Dispatches each year's rendered layer.
 * @yields {Promise} delay(1000) - Throttles dispatches between years.
 * @see {@link applyUiBand}
 * @see {@link applyNdviBand}
 * @see {@link applyMndwiBand}
 * @see {@link applyScaleFactors}
 * @see {@link maskCloudSentinel}
 * @see {@link maskCloudLandsat}
 * **Called by:
 * @see {LandCover} - via Types.ACQUIRE_IMAGES_MEDIAN.
 * @memberof module:Euscoast
 * @returns {void}
 */
const handleAcquireImagesMedian = function* ({ payload: { years } }) {
  const { geometry, satellite } = yield select(getAcquisitionParameters);
  const layers = years.map((data) => {
    const year = data[0];
    const infos = data[1];

    let mission, missionInfo, medianImage, medianImageVisualize, falseColorVisualize, missionName;

    let imagesEE = ee.List(
      infos.map((info) => {
        const date = info.date;
        let end = new Date(date);
        end.setDate(end.getDate() + 1);

        missionName = info.name;

        mission = satellite.get(missionName);

        let image = ee.ImageCollection(missionName).filterBounds(geometry).filter(ee.Filter.date(date, end)).first();

        missionInfo = missionName.split('/');

        return image;
      }),
    );

    medianImage = ee.ImageCollection.fromImages(imagesEE);
    medianImage = medianImage.filterBounds(geometry);

    medianImage = medianImage.map((img) => {
      return img
        .addBands(applyUiBand(img, mission.bands))
        .addBands(applyNdviBand(img, mission.bands))
        .addBands(applyMndwiBand(img, mission.bands));
    });

    let bands = [mission.bands.red, mission.bands.green, mission.bands.blue];
    let bandsFalseColor = [mission.bands.nir, mission.bands.green, mission.bands.blue];

    if (missionInfo[0] === 'LANDSAT') medianImage = medianImage.map(applyScaleFactors);
    medianImage = medianImage
      .map((img) => {
        if (mission.shortname === 'S2') {
          return maskCloudSentinel(img, mission);
        }

        return maskCloudLandsat(img, mission.bands.qa, geometry);
      })
      .mosaic()
      .clip(geometry);

    let visualization = {
      bands: bands,
      min: 0.0,
      max: 0.3,
    };

    medianImageVisualize = medianImage.visualize(visualization);

    if (missionInfo[0] === 'LANDSAT') {
      medianImageVisualize = medianImageVisualize.rename(bands);
    }

    var url = medianImageVisualize.getThumbURL({
      format: 'png',
      dimensions: 800,
      tileScale: 512,
    });

    let visualizationFalseColor = {
      bands: bandsFalseColor,
      min: 0.0,
      max: 0.3,
    };

    falseColorVisualize = medianImage.visualize(visualizationFalseColor);

    if (missionInfo[0] === 'LANDSAT') {
      falseColorVisualize = falseColorVisualize.rename(bands);
    }

    var falseColorUrl = falseColorVisualize.getThumbURL({
      format: 'png',
      dimensions: 800,
      tileScale: 512,
    });

    return {
      url,
      falseColorUrl,
      year,
      mission,
      imageObject: medianImage,
      classification: null,
    };
  });

  for (let i = 0; i < layers.length; i++) {
    const obj = layers[i];

    yield put(Imagery.pushImageUrl(obj.year, obj.year, obj.mission.name, obj.url, obj.falseColorUrl, obj.imageObject));
    yield delay(1000);
  }
};

/**
 * @function root
 * @description Root saga for the EUSCoast duck: registers the buffered handlers for
 * classification-to-polygon transformation and median image acquisition.
 * @generator
 * @see {@link handleTransformClassToPolygons}
 * @see {@link handleAcquireImagesMedian}
 * **Called by:
 * @see {Redux-saga middleware} - registered as the "euscoast" saga.
 * @memberof module:Euscoast
 * @returns {void}
 */
const root = function* () {
  yield all([
    bufferedHandler(Types.TRANSFORM_CLASS_TO_POLYGONS, handleTransformClassToPolygons),
    bufferedHandler(Types.ACQUIRE_IMAGES_MEDIAN, handleAcquireImagesMedian),
  ]);
};

export default root;
